import { subscribe, useSnapshot } from 'valtio';
import type { RequireAtLeastOne } from '@soundxyz/utils';
import { MintTimeStatusEnum, ReleaseAlbumRevealFilterOptionEnum } from '@soundxyz/utils/const';
import { useAuth } from '@context/auth/base';
import type { ReleasesCursorFilterArgs } from '@graphql/reactQuery';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { BASE_UPCOMING_FILTER } from '@hooks/useUpcomingDrops';
import type { RecentStatus, UpcomingStatus } from './schema';
import { FeaturedStatusEnum, ReleaseFilterStatusSchema, ReleaseStatusEnum } from './schema';
import { ReleaseStatusPersistence, releaseStatusState } from './store';

if (typeof window !== 'undefined') {
  ReleaseStatusPersistence.initialValue
    .then(value => {
      if (!value) return;
      Object.assign(releaseStatusState, value);
    })
    // eslint-disable-next-line no-console
    .catch(console.error);

  subscribe(
    releaseStatusState,
    () => {
      // Debounce storage updates to minimize CPU Usage on frequent state updates
      const pendingStorageSet = setTimeout(() => {
        const parseToStore = ReleaseFilterStatusSchema.safeParse(releaseStatusState);

        // This way we ensure that we are inserting valid data into the storage
        // reduces sentry noise too
        if (parseToStore.success) {
          ReleaseStatusPersistence.set(parseToStore.data);
        }
        clearTimeout(pendingStorageSet);
      }, 0);
    },
    true,
  );
}

export const updateUpcomingPageStatus = (state: Pick<UpcomingStatus, 'featuredStatus'>) => {
  releaseStatusState.upcoming.featuredStatus = state.featuredStatus;
};

export const updateRecentPageStatus = (
  state: RequireAtLeastOne<Pick<RecentStatus, 'featuredStatus' | 'releaseStatus'>>,
) => {
  if (state.featuredStatus) releaseStatusState.recent.featuredStatus = state.featuredStatus;
  if (state.releaseStatus) releaseStatusState.recent.releaseStatus = state.releaseStatus;
};

export const updateUpcomingScrollIndex = (index: number) => {
  releaseStatusState.upcoming.scrollIndex = index;
};

export const updateRecentScrollIndex = (index: number) => {
  releaseStatusState.recent.scrollIndex = index;
};

export function useReleaseStatus() {
  const { isAuthenticatedWithAPI, isConnectingAPIUser, authUser } = useAuth();
  const { upcoming, recent } = useSnapshot(releaseStatusState, {
    sync: true,
  });

  const userId = authUser?.id;

  /**
   * We need to reset following state if user is not authenticated
   */
  useSafeLayoutEffect(() => {
    if (isConnectingAPIUser) return;

    if (isAuthenticatedWithAPI) return;

    if (releaseStatusState.recent.featuredStatus === 'FOLLOWING')
      releaseStatusState.recent.featuredStatus = 'HOT';

    if (releaseStatusState.upcoming.featuredStatus === 'FOLLOWING')
      releaseStatusState.upcoming.featuredStatus = 'HOT';
  }, [isAuthenticatedWithAPI, isConnectingAPIUser]);

  const upcomingFilter = {
    artistFollowedByUser:
      upcoming.featuredStatus === FeaturedStatusEnum.enum.Following && userId ? userId : undefined,
    featuredStatus: upcoming.featuredStatus === FeaturedStatusEnum.enum.Hot ? 'HOT' : 'ALL',
    ...BASE_UPCOMING_FILTER,
  } satisfies ReleasesCursorFilterArgs;

  const recentFilter = {
    artistFollowedByUser:
      recent.featuredStatus === FeaturedStatusEnum.enum.Following && userId ? userId : undefined,
    featuredStatus: recent.featuredStatus === FeaturedStatusEnum.enum.Hot ? 'HOT' : 'ALL',
    releaseStatus:
      recent.releaseStatus === ReleaseStatusEnum.enum.All ? undefined : [recent.releaseStatus],
    mintTimeStatus: [MintTimeStatusEnum.PAST],
    releaseAlbumRevealStatus: ReleaseAlbumRevealFilterOptionEnum.ONLY_NOT_REVEALED_ALBUMS,
  } satisfies ReleasesCursorFilterArgs;

  return {
    upcoming,
    recent,
    updateUpcoming: updateUpcomingPageStatus,
    updateRecent: updateRecentPageStatus,
    upcomingFilter,
    recentFilter,
  };
}
